/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

/**
 * Cross-platform forecasting bridge for unified access across Android, iOS, and Web
 * Provides consistent API while adapting to platform-specific requirements
 */

// Import algorithm classes for dynamic instantiation
import forecasting.SimpleMovingAverage
import forecasting.ExponentialWeightedMovingAverage
import forecasting.SimpleExponentialSmoothing
import forecasting.LinearTrendRegression

/**
 * Time range enum for platform data context
 */
enum class TimeRange {
    DAILY, WEEKLY, MONTHLY, QUARTERLY, YEARLY
}

/**
 * Platform-agnostic forecast result
 */
data class PlatformForecastResult(
    val values: List<Double>,
    val confidence: Double,
    val methodUsed: String,
    val error: String? = null,
    val historicalValues: List<Double> = emptyList()
)

/**
 * Platform-specific input data structure
 */
data class PlatformForecastData(
    val values: List<Double>,
    val timeRange: TimeRange,
    val currency: String,
    val category: String
)

/**
 * Data converter for platform interoperability
 */
class ForecastingDataConverter {
    
    fun convertToInternalFormat(platformData: PlatformForecastData): List<Double> {
        return platformData.values
    }
    
    fun convertToPlatformFormat(result: ForecastingResult, methodId: String): PlatformForecastResult {
        return PlatformForecastResult(
            values = result.forecastValues,
            confidence = result.confidence,
            methodUsed = methodId,
            error = null,
            historicalValues = result.historicalValues
        )
    }
}

/**
 * Helper function to get the starting index for trend line visualization
 * This ensures consistency across all platforms (Android, iOS, Web)
 */
fun getTrendStartIndex(methodId: String): Int {
    return when (methodId) {
        "sma_3" -> 2 // SMA(3) starts at index 2 (needs 3 points)
        "sma_5" -> 4 // SMA(5) starts at index 4 (needs 5 points)
        "ewma_03", "ewma_06" -> 3 // EWMA starts at index 3 (windowSize = 3)
        "ses_06" -> 1 // SES starts at index 1 (matching iOS - first point not included)
        "ltr_default" -> 0 // Linear trend starts from beginning
        else -> 2 // Default to 2 for unknown methods
    }
}

/**
 * Unified forecasting bridge - main entry point for all platforms
 */
class UnifiedForecastingBridge(
    private val memoizedService: MemoizedForecastingService = MemoizedForecastingService(),
    private val methodSelector: ForecastingMethodSelector = ForecastingMethodSelector(),
    private val converter: ForecastingDataConverter = ForecastingDataConverter()
) {
    
    private var isInitialized = true
    private var defaultMethodId: String? = null
    
    /**
     * Check if bridge is ready
     */
    fun isReady(): Boolean = isInitialized
    
    /**
     * Get available forecasting methods
     */
    fun getAvailableMethods(): List<ForecastingMethod> = methodSelector.getAvailableMethods()
    
    /**
     * Forecast using best algorithm selection
     */
    fun forecast(data: List<Double>, periods: Int): PlatformForecastResult {
        return try {
            val result = memoizedService.forecastWithBestAlgorithm(data, periods)
            converter.convertToPlatformFormat(result, "auto")
        } catch (e: Exception) {
            PlatformForecastResult(
                values = emptyList(),
                confidence = 0.0,
                methodUsed = "error",
                error = e.message
            )
        }
    }
    
    /**
     * Forecast with specific method
     */
    fun forecastWithMethod(methodId: String, data: List<Double>, periods: Int): PlatformForecastResult {
        return try {
            val method = methodSelector.getAvailableMethods().find { it.id == methodId }
            if (method != null && method.isEnabled) {
                // Create algorithm instance with method-specific parameters
                val result = forecastWithMethodParameters(method, data, periods)
                converter.convertToPlatformFormat(result, methodId)
            } else {
                // Fallback to best algorithm
                forecast(data, periods)
            }
        } catch (e: Exception) {
            PlatformForecastResult(
                values = emptyList(),
                confidence = 0.0,
                methodUsed = methodId,
                error = e.message
            )
        }
    }

    /**
     * Create algorithm instance dynamically based on method parameters
     */
    private fun forecastWithMethodParameters(method: ForecastingMethod, data: List<Double>, periods: Int): ForecastingResult {
        println("🔍 DEBUG: forecastWithMethodParameters called for method: ${method.id} (${method.algorithmName})")
        println("🔍 DEBUG: Method parameters: ${method.parameters}")

        val algorithm = when (method.algorithmName) {
            "SimpleMovingAverage" -> {
                val period = method.parameters["period"] as? Int ?: 5
                println("🔍 DEBUG: Creating SimpleMovingAverage with period=$period")
                SimpleMovingAverage(period)
            }
            "ExponentialWeightedMovingAverage" -> {
                val alpha = method.parameters["alpha"] as? Double ?: 0.3
                println("🔍 DEBUG: Creating ExponentialWeightedMovingAverage with alpha=$alpha")
                ExponentialWeightedMovingAverage(alpha)
            }
            "SimpleExponentialSmoothing" -> {
                val alpha = method.parameters["alpha"] as? Double ?: 0.3
                println("🔍 DEBUG: Creating SimpleExponentialSmoothing with alpha=$alpha")
                SimpleExponentialSmoothing(alpha)
            }
            "LinearTrendRegression" -> {
                println("🔍 DEBUG: Creating LinearTrendRegression")
                LinearTrendRegression()
            }
            else -> {
                println("🔍 DEBUG: Unknown algorithm ${method.algorithmName}, using fallback")
                // Fallback to registered algorithms
                return memoizedService.forecastWithAlgorithm(method.algorithmName, data, periods)
            }
        }

        val result = algorithm.forecast(data, periods)
        println("🔍 DEBUG: Algorithm ${method.algorithmName} produced forecast: ${result.forecastValues.take(3)}")
        return result
    }
    
    /**
     * Select default method for auto selection
     */
    fun selectDefaultMethod(methodId: String) {
        defaultMethodId = methodId
    }
    
    /**
     * Get currently selected default method
     */
    fun getDefaultMethod(): String? = defaultMethodId

    /**
     * Calculate historical trend values using specific method (replaces Android's calculateHistoricalTrend)
     */
    fun calculateHistoricalTrend(data: List<Double>, methodId: String): List<Double> {
        if (data.isEmpty()) return emptyList()

        val result = forecastWithMethod(methodId, data, 0) // 0 periods for forecast, just want historical
        return result.historicalValues
    }
}

// ===== ANDROID-SPECIFIC BRIDGE =====

/**
 * Android chart data structure
 */
data class AndroidChartData(
    val hasHistoricalSeries: Boolean,
    val hasForecastSeries: Boolean,
    val colors: Map<String, String>
)

/**
 * Android forecast result
 */
data class AndroidForecastResult(
    val isSuccess: Boolean,
    val forecastValues: List<Double>,
    val chartData: AndroidChartData,
    val methodUsed: String
)

/**
 * Android-specific forecasting bridge
 */
class AndroidForecastingBridge(
    private val unifiedBridge: UnifiedForecastingBridge = UnifiedForecastingBridge()
) {
    
    /**
     * Calculate historical trend values using shared algorithms (replaces Android's native implementation)
     */
    fun calculateHistoricalTrend(data: List<Double>, methodId: String): List<Double> {
        return unifiedBridge.calculateHistoricalTrend(data, methodId)
    }

    /**
     * Forecast for Android trends with DoubleArray compatibility
     */
    fun forecastForTrends(data: DoubleArray, periods: Int, methodId: String): AndroidForecastResult {
        val dataList = data.toList()
        val actualMethodId = if (methodId == "auto") {
            unifiedBridge.getDefaultMethod() ?: "sma_5"
        } else methodId
        
        val result = unifiedBridge.forecastWithMethod(actualMethodId, dataList, periods)
        
        return AndroidForecastResult(
            isSuccess = result.error == null,
            forecastValues = result.values,
            chartData = AndroidChartData(
                hasHistoricalSeries = true,
                hasForecastSeries = result.values.isNotEmpty(),
                colors = mapOf(
                    "historical" to "#2196F3",
                    "forecast" to "#FF5722",
                    "trend" to "#4CAF50"
                )
            ),
            methodUsed = actualMethodId
        )
    }
}

// ===== iOS-SPECIFIC BRIDGE =====

/**
 * iOS chart point structure
 */
data class IOSChartPoint(
    val x: Double,
    val y: Double,
    val hasValidCoordinates: Boolean = true
)

/**
 * iOS forecast result for SwiftUI
 */
data class IOSForecastResult(
    val success: Boolean,
    val predictions: List<Double>,
    val chartPoints: List<IOSChartPoint>,
    val metadata: Map<String, String>
)

/**
 * iOS-specific forecasting bridge
 */
class IOSForecastingBridge(
    private val unifiedBridge: UnifiedForecastingBridge = UnifiedForecastingBridge()
) {
    
    /**
     * Secondary constructor for Swift interop
     */
    constructor() : this(UnifiedForecastingBridge())
    
    /**
     * Get available forecasting methods
     */
    fun getAvailableMethods(): List<ForecastingMethod> = unifiedBridge.getAvailableMethods()

    /**
     * Calculate historical trend values using shared algorithms (replaces iOS's native implementation)
     */
    fun calculateHistoricalTrend(data: List<Double>, methodId: String): List<Double> {
        return unifiedBridge.calculateHistoricalTrend(data, methodId)
    }
    
    /**
     * Forecast for SwiftUI components
     */
    fun forecastForSwiftUI(data: List<Double>, periods: Int, selectedMethod: String): IOSForecastResult {
        val result = unifiedBridge.forecastWithMethod(selectedMethod, data, periods)
        
        // Create chart points for SwiftUI
        val chartPoints = result.values.mapIndexed { index, value ->
            IOSChartPoint(
                x = (data.size + index).toDouble(),
                y = value,
                hasValidCoordinates = value.isFinite()
            )
        }
        
        return IOSForecastResult(
            success = result.error == null,
            predictions = result.values,
            chartPoints = chartPoints,
            metadata = mapOf(
                "algorithm" to result.methodUsed,
                "confidence" to "${(result.confidence * 100).toInt() / 100.0}",
                "dataPoints" to data.size.toString(),
                "forecastPeriods" to periods.toString()
            )
        )
    }
}

// ===== WEB-SPECIFIC BRIDGE =====

/**
 * Web visualization configuration
 */
data class WebVisualizationConfig(
    val isInteractive: Boolean,
    val chartType: String = "line",
    val showLegend: Boolean = true
)

/**
 * Web legend entry
 */
data class WebLegendEntry(
    val label: String,
    val color: String,
    val type: String
)

/**
 * Web legend configuration
 */
data class WebLegendConfig(
    val entries: List<WebLegendEntry>
)

/**
 * Web forecast result
 */
data class WebForecastResult(
    val status: String,
    val data: List<Double>,
    val visualization: WebVisualizationConfig,
    val legend: WebLegendConfig
)

/**
 * Web-specific forecasting bridge
 */
class WebForecastingBridge(
    private val unifiedBridge: UnifiedForecastingBridge = UnifiedForecastingBridge()
) {

    /**
     * Calculate historical trend values using shared algorithms (replaces Web's native implementation)
     */
    fun calculateHistoricalTrend(data: List<Double>, methodId: String): List<Double> {
        return unifiedBridge.calculateHistoricalTrend(data, methodId)
    }

    /**
     * Forecast for web components
     */
    fun forecastForWebComponents(data: List<Double>, periods: Int, methodSelection: String): WebForecastResult {
        val result = unifiedBridge.forecastWithMethod(methodSelection, data, periods)

        val legendEntries = listOf(
            WebLegendEntry("Historical", "#2196F3", "solid"),
            WebLegendEntry("Forecast", "#FF5722", "dashed")
        )

        return WebForecastResult(
            status = if (result.error == null) "success" else "error",
            data = result.values,
            visualization = WebVisualizationConfig(
                isInteractive = true,
                chartType = "line",
                showLegend = true
            ),
            legend = WebLegendConfig(entries = legendEntries)
        )
    }
}